import { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  Upload,
  Loader2,
  Lock,
  X,
  Image as ImageIcon,
  Languages,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { useLanguage } from "../context/LanguageContext";
import { useBlogTranslation } from "../translations";
import axios from "axios";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ──────── Auth Gate ──────── */
const PasswordGate = ({ onAuth, t }) => {
  const [pw, setPw] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/blog/verify-password`, null, {
        headers: { "x-admin-password": pw },
      });
      onAuth(pw);
    } catch {
      setError(t.admin.wrongPassword);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#F7F9FC] flex items-center justify-center px-4"
      data-testid="blog-admin-login"
    >
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-sm bg-white border border-[#D9D9D9] rounded-2xl p-8 shadow-sm"
      >
        <div className="flex justify-center mb-6">
          <div className="w-14 h-14 rounded-2xl bg-[#0047AB]/10 flex items-center justify-center">
            <Lock className="w-7 h-7 text-[#0047AB]" />
          </div>
        </div>
        <h1
          className="text-xl font-bold text-center text-[#1B1B1B] mb-1"
          data-testid="admin-login-title"
        >
          {t.admin.login}
        </h1>
        <p className="text-sm text-[#5E6C83] text-center mb-6">Cayus Blog</p>

        <Label className="text-[#3A3F4B] mb-1.5 block text-sm">
          {t.admin.passwordLabel}
        </Label>
        <Input
          type="password"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          placeholder={t.admin.passwordPlaceholder}
          className="mb-3"
          data-testid="admin-password-input"
        />
        {error && (
          <p className="text-sm text-red-500 mb-3" data-testid="admin-login-error">
            {error}
          </p>
        )}
        <Button
          type="submit"
          disabled={loading || !pw}
          className="w-full bg-[#0047AB] hover:bg-[#0B3F8F] text-white"
          data-testid="admin-login-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t.admin.enter}
        </Button>
      </form>
    </div>
  );
};

/* ──────── Editor Panel ──────── */
const ArticleEditor = ({ article, password, onSaved, onCancel, t }) => {
  const [activeLang, setActiveLang] = useState("fr");
  const [titleEn, setTitleEn] = useState("");
  const [titleFr, setTitleFr] = useState("");
  const [titleEs, setTitleEs] = useState("");
  const [contentEn, setContentEn] = useState("");
  const [contentFr, setContentFr] = useState("");
  const [contentEs, setContentEs] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [published, setPublished] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [translating, setTranslating] = useState(false);
  const fileInputRef = useRef(null);

  const titles = { en: titleEn, fr: titleFr, es: titleEs };
  const setTitles = { en: setTitleEn, fr: setTitleFr, es: setTitleEs };
  const contents = { en: contentEn, fr: contentFr, es: contentEs };
  const setContents = { en: setContentEn, fr: setContentFr, es: setContentEs };

  useEffect(() => {
    if (article) {
      setTitleEn(article.title_en || "");
      setTitleFr(article.title_fr || "");
      setTitleEs(article.title_es || "");
      setContentEn(article.content_en || "");
      setContentFr(article.content_fr || "");
      setContentEs(article.content_es || "");
      setImageUrl(article.image_url || "");
      setPublished(article.published || false);
    }
  }, [article]);

  const headers = { "x-admin-password": password };

  const quillModules = useMemo(
    () => ({
      toolbar: [
        [{ header: [1, 2, 3, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ list: "ordered" }, { list: "bullet" }],
        ["blockquote", "link", "image"],
        ["clean"],
      ],
    }),
    []
  );

  const buildPayload = () => ({
    title_en: titleEn, title_fr: titleFr, title_es: titleEs,
    content_en: contentEn, content_fr: contentFr, content_es: contentEs,
    image_url: imageUrl, published,
  });

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/blog/upload-image`, formData, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      setImageUrl(res.data.url);
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async (overridePublished) => {
    setSaving(true);
    try {
      const payload = buildPayload();
      if (overridePublished !== undefined) {
        payload.published = overridePublished;
      }
      if (article) {
        await axios.put(`${API}/blog/articles/${article.id}`, payload, { headers });
      } else {
        await axios.post(`${API}/blog/articles`, payload, { headers });
      }
      toast.success(t.admin.savedOk);
      onSaved();
    } catch {
      toast.error(t.admin.errorSave);
    } finally {
      setSaving(false);
    }
  };

  const handleTranslate = async () => {
    // Auto-detect which language has content to translate from
    let sourceLang = activeLang;
    let sourceTitle = titles[sourceLang];
    let sourceContent = contents[sourceLang];

    // If current tab is empty, find a tab with content
    const isContentEmpty = (t, c) => !t?.trim() && (!c?.trim() || c === "<p><br></p>");
    if (isContentEmpty(sourceTitle, sourceContent)) {
      for (const lang of ["en", "fr", "es"]) {
        if (!isContentEmpty(titles[lang], contents[lang])) {
          sourceLang = lang;
          sourceTitle = titles[lang];
          sourceContent = contents[lang];
          break;
        }
      }
    }

    if (isContentEmpty(sourceTitle, sourceContent)) {
      toast.error(
        activeLang === "fr" ? "Rédigez d'abord le contenu dans au moins une langue" :
        activeLang === "es" ? "Escriba primero el contenido en al menos un idioma" :
        "Write content in at least one language first"
      );
      return;
    }

    const targetLangs = ["en", "fr", "es"].filter((l) => l !== sourceLang);
    setTranslating(true);
    try {
      const res = await axios.post(
        `${API}/blog/translate`,
        { title: sourceTitle, content: sourceContent, source_lang: sourceLang, target_langs: targetLangs },
        { headers, timeout: 120000 }
      );
      let translated = 0;
      for (const lang of targetLangs) {
        if (res.data[lang] && !res.data[lang].error) {
          if (res.data[lang].title) { setTitles[lang](res.data[lang].title); translated++; }
          if (res.data[lang].content) { setContents[lang](res.data[lang].content); translated++; }
        }
      }
      if (translated > 0) {
        toast.success(
          activeLang === "fr" ? "Traduction terminée ! Vérifiez les onglets." :
          activeLang === "es" ? "¡Traducción completada! Revise las pestañas." :
          "Translation complete! Check the other tabs."
        );
      } else {
        toast.error(
          activeLang === "fr" ? "La traduction a échoué, veuillez réessayer." :
          "Translation failed, please try again."
        );
      }
    } catch {
      toast.error(
        activeLang === "fr" ? "Erreur de traduction. Réessayez dans quelques secondes." :
        "Translation error. Please try again in a few seconds."
      );
    } finally {
      setTranslating(false);
    }
  };

  const coverUrl = imageUrl
    ? imageUrl.startsWith("/api")
      ? `${process.env.REACT_APP_BACKEND_URL}${imageUrl}`
      : imageUrl
    : null;

  const langTabs = ["fr", "en", "es"];

  return (
    <div className="space-y-6" data-testid="article-editor">
      {/* Lang tabs */}
      <div className="flex gap-1 bg-[#F3F3F3] p-1 rounded-xl w-fit" data-testid="editor-lang-tabs">
        {langTabs.map((l) => (
          <button
            key={l}
            onClick={() => setActiveLang(l)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeLang === l
                ? "bg-white text-[#0047AB] shadow-sm"
                : "text-[#5E6C83] hover:text-[#3A3F4B]"
            }`}
            data-testid={`editor-lang-${l}`}
          >
            {t.admin.langTabs[l]}
          </button>
        ))}
      </div>

      {/* Title */}
      <div>
        <Label className="text-[#3A3F4B] mb-1.5 block text-sm">
          {t.admin.titleLabel} ({activeLang.toUpperCase()})
        </Label>
        <Input
          value={titles[activeLang]}
          onChange={(e) => setTitles[activeLang](e.target.value)}
          placeholder={`${t.admin.titleLabel} (${activeLang.toUpperCase()})`}
          data-testid="editor-title-input"
        />
      </div>

      {/* Rich text content - key forces full remount per lang to prevent cross-tab contamination */}
      <div>
        <Label className="text-[#3A3F4B] mb-1.5 block text-sm">
          {t.admin.contentLabel} ({activeLang.toUpperCase()})
        </Label>
        <div className="border border-[#D9D9D9] rounded-xl overflow-hidden" data-testid="editor-content-quill">
          <ReactQuill
            key={activeLang}
            theme="snow"
            defaultValue={contents[activeLang]}
            onChange={(val) => setContents[activeLang](val)}
            modules={quillModules}
            className="bg-white [&_.ql-editor]:min-h-[280px]"
          />
        </div>
      </div>

      {/* Cover image */}
      <div>
        <Label className="text-[#3A3F4B] mb-1.5 block text-sm">{t.admin.imageLabel}</Label>
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="gap-2"
            data-testid="editor-upload-btn"
          >
            {uploading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Upload className="w-4 h-4" />
            )}
            {uploading ? t.admin.uploading : t.admin.uploadImage}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleUpload}
            className="hidden"
            data-testid="editor-file-input"
          />
          {coverUrl && (
            <div className="relative group">
              <img
                src={coverUrl}
                alt="cover"
                className="h-16 w-24 object-cover rounded-lg border border-[#D9D9D9]"
                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
              />
              <div className="h-16 w-24 rounded-lg border-2 border-dashed border-red-200 bg-red-50 items-center justify-center hidden text-xs text-[#F87171] text-center px-1">
                Image perdue, rechargez
              </div>
              <button
                onClick={() => setImageUrl("")}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          )}
          {!coverUrl && (
            <div className="h-16 w-24 rounded-lg border-2 border-dashed border-[#D9D9D9] flex items-center justify-center">
              <ImageIcon className="w-5 h-5 text-[#E4EEFC]" />
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-3 pt-2">
        <Button
          onClick={() => handleSave(undefined)}
          disabled={saving}
          className="bg-[#0047AB] hover:bg-[#0B3F8F] text-white gap-2"
          data-testid="editor-save-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          {saving ? t.admin.saving : t.admin.save}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={handleTranslate}
          disabled={translating}
          className="gap-2 border-violet-200 text-violet-600 hover:bg-violet-50"
          data-testid="editor-translate-btn"
        >
          {translating ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Languages className="w-4 h-4" />
          )}
          {translating ? t.admin.translating : t.admin.translate}
        </Button>
        {published ? (
          <Button
            type="button"
            variant="outline"
            onClick={() => handleSave(false)}
            disabled={saving}
            className="gap-2 text-amber-600 border-amber-200 hover:bg-amber-50"
            data-testid="editor-unpublish-btn"
          >
            <EyeOff className="w-4 h-4" />
            {t.admin.unpublish}
          </Button>
        ) : (
          <Button
            type="button"
            onClick={() => handleSave(true)}
            disabled={saving}
            className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="editor-publish-btn"
          >
            <Eye className="w-4 h-4" />
            {t.admin.publish}
          </Button>
        )}
        <Button
          type="button"
          variant="ghost"
          onClick={onCancel}
          data-testid="editor-cancel-btn"
        >
          <X className="w-4 h-4 mr-1" /> Cancel
        </Button>
      </div>
    </div>
  );
};

/* ──────── Main Admin Page ──────── */
const BlogAdminPage = () => {
  const { language } = useLanguage();
  const t = useBlogTranslation(language);
  const [password, setPassword] = useState(() => sessionStorage.getItem("blog-admin-pw") || "");
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null | "new" | article object
  const [deleting, setDeleting] = useState(null);

  const headers = { "x-admin-password": password };

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/blog/articles?all=true`, { headers });
      setArticles(res.data);
    } catch {
      toast.error("Failed to load articles");
    } finally {
      setLoading(false);
    }
  };

  const handleAuth = (pw) => {
    sessionStorage.setItem("blog-admin-pw", pw);
    setPassword(pw);
  };

  useEffect(() => {
    if (password) fetchArticles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password]);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/blog/articles/${id}`, { headers });
      toast.success(t.admin.deletedOk);
      setDeleting(null);
      fetchArticles();
    } catch {
      toast.error(t.admin.errorDelete);
    }
  };

  if (!password) {
    return <PasswordGate onAuth={handleAuth} t={t} />;
  }

  const getTitle = (a) => a.title_fr || a.title_en || a.title_es || "(sans titre)";

  return (
    <div className="min-h-screen bg-[#F7F9FC]" data-testid="blog-admin-page">
      {/* Top bar */}
      <header className="bg-white border-b border-[#D9D9D9] sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <h1 className="font-bold text-lg text-[#1B1B1B]">{t.admin.title}</h1>
          <Link
            to="/"
            className="text-sm text-[#5E6C83] hover:text-[#1B1B1B] flex items-center gap-1.5 transition-colors"
            data-testid="admin-back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            {t.backToHome}
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {editing !== null ? (
          <ArticleEditor
            article={editing === "new" ? null : editing}
            password={password}
            t={t}
            onSaved={() => {
              setEditing(null);
              fetchArticles();
            }}
            onCancel={() => setEditing(null)}
          />
        ) : (
          <>
            {/* Toolbar */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-[#1B1B1B]">
                {t.admin.articles}{" "}
                <span className="text-sm font-normal text-[#9098A3]">({articles.length})</span>
              </h2>
              <Button
                onClick={() => setEditing("new")}
                className="bg-[#0047AB] hover:bg-[#0B3F8F] text-white gap-2"
                data-testid="admin-new-article-btn"
              >
                <Plus className="w-4 h-4" />
                {t.admin.newArticle}
              </Button>
            </div>

            {loading ? (
              <div className="flex justify-center py-20">
                <div className="w-8 h-8 border-2 border-[#D9D9D9] border-t-[#0047AB] rounded-full animate-spin" />
              </div>
            ) : articles.length === 0 ? (
              <div className="text-center py-20 text-[#9098A3]" data-testid="admin-no-articles">
                <p>{t.admin.noArticles}</p>
              </div>
            ) : (
              <div className="flex flex-col gap-3" data-testid="admin-articles-list">
                {articles.map((a) => (
                  <div
                    key={a.id}
                    className="bg-white border border-[#D9D9D9] rounded-xl p-4 flex items-center gap-4 hover:border-[#C3CFE0] transition-colors"
                    data-testid={`admin-article-${a.id}`}
                  >
                    {/* Thumbnail */}
                    {a.image_url ? (
                      <img
                        src={a.image_url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${a.image_url}` : a.image_url}
                        alt=""
                        className="w-16 h-12 object-cover rounded-lg flex-shrink-0 border border-[#ECECEC]"
                        onError={(e) => { e.target.onerror = null; e.target.src = ''; e.target.className = 'w-16 h-12 rounded-lg bg-red-50 flex-shrink-0 border border-red-200'; }}
                      />
                    ) : (
                      <div className="w-16 h-12 rounded-lg bg-[#F3F3F3] flex items-center justify-center flex-shrink-0">
                        <ImageIcon className="w-5 h-5 text-[#E4EEFC]" />
                      </div>
                    )}

                    {/* Title + meta */}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-[#1B1B1B] truncate">{getTitle(a)}</p>
                      <p className="text-xs text-[#9098A3] mt-0.5">
                        {a.published ? (
                          <span className="text-[#10B981] font-medium">{t.admin.published}</span>
                        ) : (
                          <span className="text-amber-500 font-medium">{t.admin.draft}</span>
                        )}
                        {" · "}
                        {new Date(a.created_at).toLocaleDateString()}
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setEditing(a)}
                        data-testid={`admin-edit-${a.id}`}
                      >
                        <Pencil className="w-4 h-4 text-[#5E6C83]" />
                      </Button>
                      {deleting === a.id ? (
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 text-xs"
                            onClick={() => handleDelete(a.id)}
                            data-testid={`admin-confirm-delete-${a.id}`}
                          >
                            {t.admin.delete}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeleting(null)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleting(a.id)}
                          data-testid={`admin-delete-${a.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-[#9098A3] hover:text-red-500" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </main>

      {/* Delete confirmation overlay */}
      {deleting && (
        <div className="sr-only" aria-live="polite">
          {t.admin.deleteConfirm}
        </div>
      )}
    </div>
  );
};

export default BlogAdminPage;
